import React, { useState, useEffect } from 'react';

import Checkbox from '@material-ui/core/Checkbox';
import Table from '@material-ui/core/Table';
import TableBody from '@material-ui/core/TableBody';
import TableCell from '@material-ui/core/TableCell';
import TableHead from '@material-ui/core/TableHead';
import TableRow from '@material-ui/core/TableRow';

import * as Api from '../../../api/api';
import { UiButton } from '../../../ui/ui-button';
import { UiDialog } from '../../../ui/ui-dialog';

import { employeeNameFromSimpleContact } from '../../../employees/utils/employee-name';
import Message from '../../../widgets/legacy-message';
import { ReactComponent as LoadingLogo } from '../../../widgets/loading-logo.svg';
import { TableHeaderCell } from '../../../widgets/table-components/table-header-cell';

type Props = EmployeeDirectoryWizardStepProps;

export const EmployeeDirectoryImportWizardRemoveEmployees = ({
  employeeDirectoryImportId,
  onNext,
  onCancel,
  PageIndicator,
}: Props) => {
  const [employeesToRemove, setEmployeesToRemove] = useState<Array<
    ImportTableRow & { employeeId: number }
  > | null>(null);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [hasInitialized, setHasInitialized] = useState<boolean>(false);
  const [isSaving, setIsSaving] = useState<boolean>(false);
  const [selectedEmployeeIds, setSelectedEmployeeIds] = useState<Array<number>>([]);

  useEffect(() => {
    if (!hasInitialized) {
      Api.get(`/employee-directory-import/${employeeDirectoryImportId}/remove-employees`)
        .then((resp) => {
          resp.json().then((data: ImportTableRow[] | { error: string }) => {
            if ('errorMessage' in data || !Array.isArray(data)) {
              setErrorMessage('Failed to load data');
            } else if (data.length === 0) {
              onNext();
            } else {
              const employeesToRemoveWithIds = data.filter(
                (emp: ImportTableRow): emp is ImportTableRow & { employeeId: number } =>
                  !!emp.employeeId,
              );
              setEmployeesToRemove(employeesToRemoveWithIds);
            }
          });
        })
        .catch(() => {
          setErrorMessage('Failed to load data');
        });
      setHasInitialized(true);
    }
  }, [hasInitialized, employeeDirectoryImportId, onNext]);

  const handleToggleEmployee = (employeeId: number | null) => () => {
    if (!employeeId) {
      return;
    }
    if (selectedEmployeeIds.includes(employeeId)) {
      // deselecting
      setSelectedEmployeeIds(selectedEmployeeIds.filter((id) => id !== employeeId));
    } else {
      const newSelectedIds = selectedEmployeeIds.includes(employeeId)
        ? selectedEmployeeIds
        : [...selectedEmployeeIds, employeeId];
      setSelectedEmployeeIds(newSelectedIds);
    }
  };

  const handleSelectAll = () => {
    const allEmployeeIds = employeesToRemove?.map((emp) => emp.employeeId) || [];
    setSelectedEmployeeIds(allEmployeeIds);
  };

  const handleDeselectAll = () => setSelectedEmployeeIds([]);

  const handleOnRemoveEmployees = async () => {
    let response = null;
    setIsSaving(true);
    try {
      response = await Api.post(
        `/employee-directory-import/${employeeDirectoryImportId}/remove-employees`,
        { body: JSON.stringify({ employeeIds: selectedEmployeeIds }) },
      );
    } catch (e) {
      setErrorMessage('There was an error saving data');
    }
    const result = await response?.json();
    setIsSaving(false);
    if (!result || result.errorMessage) {
      setErrorMessage('There was an error saving data');
    } else {
      onNext();
    }
  };

  const handleOnKeepEmployees = async () => {
    onNext();
  };

  const allEmployeesSelected =
    selectedEmployeeIds.length === employeesToRemove?.filter((emp) => !!emp.employeeId)?.length;

  return (
    <>
      <UiDialog.Content>
        <PageIndicator />
        <span className="typo-h5">Remove Employees</span>
        <p className="typo-subtitle">
          These are the employees in this account that were not present in the import data. Select
          those to mark as terminated, unselected employees will be kept.
        </p>
        {errorMessage ? <Message variant="error">{errorMessage}</Message> : null}
        {!employeesToRemove && !errorMessage ? (
          <div className="u-textAlign-center">
            <LoadingLogo />
          </div>
        ) : null}
        {employeesToRemove ? (
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableHeaderCell
                  onClick={allEmployeesSelected ? handleDeselectAll : handleSelectAll}
                  className="typo-link-secondary"
                >
                  {allEmployeesSelected ? 'Deselect' : 'Select'} All
                </TableHeaderCell>
                <TableHeaderCell>Name</TableHeaderCell>
                <TableHeaderCell>Email</TableHeaderCell>
                <TableHeaderCell>Phone Number</TableHeaderCell>
                <TableHeaderCell />
              </TableRow>
            </TableHead>
            <TableBody>
              {employeesToRemove.map((emp) => (
                <TableRow key={emp.employeeId}>
                  <TableCell>
                    <Checkbox
                      checked={!!selectedEmployeeIds.includes(emp.employeeId)}
                      onChange={handleToggleEmployee(emp.employeeId)}
                    />
                  </TableCell>
                  <TableCell>{employeeNameFromSimpleContact(emp)}</TableCell>
                  <TableCell>{emp.email}</TableCell>
                  <TableCell>{emp.phoneNumber}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        ) : null}
      </UiDialog.Content>
      <UiDialog.Actions>
        <UiButton variant="dismiss" onClick={onCancel}>
          Cancel
        </UiButton>
        <div>
          <UiButton
            variant="secondary"
            disabled={Boolean(errorMessage) || !selectedEmployeeIds.length}
            onClick={handleOnRemoveEmployees}
            loading={isSaving}
          >
            Mark Selected Terminated
          </UiButton>
          <UiButton
            variant="primary"
            className="u-margin-left"
            disabled={Boolean(errorMessage)}
            onClick={handleOnKeepEmployees}
            loading={isSaving}
          >
            Keep All
          </UiButton>
        </div>
      </UiDialog.Actions>
    </>
  );
};
